import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionComprasGet } from "../../redux/actions/compras/compras";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ESTATUS_LABEL = { pendiente: "Pendiente", parcial: "Pago parcial", pagada: "Pagada" };

export default function ComprasPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.compras);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionComprasGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    pendientes: items.filter((c) => !c.cancelada && c.estatus_nombre !== "pagada").length,
    canceladas: items.filter((c) => Boolean(c.cancelada)).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "pendientes") return items.filter((c) => !c.cancelada && c.estatus_nombre !== "pagada");
    if (activeFilter === "canceladas") return items.filter((c) => Boolean(c.cancelada));
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de compras", icon: <i className="fa-solid fa-cart-arrow-down" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "pendientes", label: "Pendientes de pago", icon: <i className="fa-solid fa-clock" />, iconCls: "paq-stat-icon-barra", count: counts.pendientes },
    { key: "canceladas", label: "Canceladas", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-receta", count: counts.canceladas },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Compras</Title>
                <Text className="paq-subtitle">Historial de compras a proveedores.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por proveedor o folio de factura..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Compras ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} compras encontradas</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/compras/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Nueva compra
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin compras que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((c) => (
                        <div key={c.id_compra} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className="fa-solid fa-cart-arrow-down paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{c.proveedor_nombre}</Paragraph>
                              <span className="paq-tipo-badge paq-tipo-sonido">{money(c.total)}</span>
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-file-invoice paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Folio factura</span>
                                <span className="paq-info-value">{c.folio_factura || "Sin folio"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="paq-card-footer">
                            <div className={`paq-bottom-strip ${c.cancelada ? "is-red" : c.estatus_nombre === "pagada" ? "is-green" : "is-red"}`}>
                              <span className="paq-strip-label">
                                {c.cancelada ? "Cancelada" : ESTATUS_LABEL[c.estatus_nombre] || c.estatus_nombre}
                              </span>
                            </div>
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/compras/${c.id_compra}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
